import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";

const STAGES = ["new_lead", "contacted", "booked_test_drive", "warm_lead", "offer_sent", "won", "lost"];
const stageLabels = {
    new_lead: "Ny lead", contacted: "Kontaktad", booked_test_drive: "Bokad provkörning",
    warm_lead: "Varm lead", offer_sent: "Offert skickad", won: "Vunnen", lost: "Förlorad",
};
const stageColors = {
    new_lead: "border-l-blue-500", contacted: "border-l-purple-500",
    booked_test_drive: "border-l-orange-500", warm_lead: "border-l-yellow-500",
    offer_sent: "border-l-indigo-500", won: "border-l-green-500", lost: "border-l-red-500",
};

export default function AdminPipeline() {
    const [pipeline, setPipeline] = useState({});
    const [loading, setLoading] = useState(true);

    const fetchPipeline = () => {
        api.get("/contacts/pipeline/summary").then((r) => setPipeline(r.data)).catch(console.error).finally(() => setLoading(false));
    };

    useEffect(() => { fetchPipeline(); }, []);

    const moveContact = async (contactId, newStage) => {
        try {
            await api.put(`/contacts/${contactId}`, { pipeline_stage: newStage });
            toast.success("Kontakt flyttad");
            fetchPipeline();
        } catch { toast.error("Misslyckades"); }
    };

    if (loading) return <div className="p-8 text-neutral-400">Laddar...</div>;

    return (
        <div className="p-8" data-testid="admin-pipeline">
            <h1 className="text-2xl font-medium tracking-tight mb-6">Pipeline</h1>
            <div className="flex gap-4 overflow-x-auto pb-4">
                {STAGES.map((stage) => (
                    <div key={stage} className="min-w-[260px] flex-shrink-0" data-testid={`pipeline-stage-${stage}`}>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-xs uppercase tracking-wider text-neutral-500 font-medium">{stageLabels[stage]}</h3>
                            <Badge variant="outline" className="text-[10px]">{pipeline[stage]?.length || 0}</Badge>
                        </div>
                        <div className="space-y-2">
                            {(pipeline[stage] || []).map((contact) => (
                                <Card key={contact.id} className={`pipeline-card bg-white border-neutral-200 shadow-none rounded-md border-l-4 ${stageColors[stage]}`} data-testid={`pipeline-card-${contact.id}`}>
                                    <CardContent className="p-3">
                                        <Link to={`/admin/contacts/${contact.id}`} className="hover:underline">
                                            <div className="text-sm font-medium">{contact.name}</div>
                                        </Link>
                                        <div className="text-xs text-neutral-400 mb-2">{contact.email}</div>
                                        <Select value={stage} onValueChange={(val) => moveContact(contact.id, val)}>
                                            <SelectTrigger className="h-7 text-xs" data-testid={`move-contact-${contact.id}`}>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {STAGES.map((s) => <SelectItem key={s} value={s}>{stageLabels[s]}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </CardContent>
                                </Card>
                            ))}
                            {(!pipeline[stage] || !pipeline[stage].length) && (
                                <div className="p-4 text-xs text-neutral-400 text-center border border-dashed border-neutral-200 rounded-md">Inga kontakter</div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
